import { getTimeSinceDate } from '@airship-platform/airship-patterns';
import {
  Column,
  FlexBox,
  LoadingButton,
  ResponsiveTable,
} from '@airship-platform/jam-web/components';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router';
import { SectionWrapper } from '../../patterns';
import {
  formatBytes,
  type StorageEstimateInfo,
} from '../../data-offline/rxdb/utils/storageEstimate';
import {
  OfflineCollectionInfo,
  useOfflineDiagnostics,
} from './useOfflineDiagnostics';

const renderSyncStatus = (sync: {
  error?: string | null;
  lastSyncTime?: string;
}) => {
  if (sync.error) {
    return `${sync.error}`;
  }
  if (sync.lastSyncTime) {
    return `Last Sync: ${getTimeSinceDate(sync.lastSyncTime)}`;
  }
  return 'Last Sync: no sync data';
};

const renderPersistence = (storage: StorageEstimateInfo) => {
  if (storage.persisted) {
    return 'Storage is persisted - the browser will not evict it under pressure.';
  }
  if (!storage.persistSupported) {
    return 'This browser cannot persist storage, so it may evict offline data under pressure. Installing to the home screen is the only protection.';
  }
  return 'Storage is best-effort - the browser may evict offline data under pressure. Opting in above requests persistence.';
};

const OfflineDiagnosticsPage: React.FC = () => {
  const {
    isLoading,
    isSyncing,
    dbName,
    collections,
    storage,
    lookupsSync,
    pricebookSync,
    modifiersSync,
    membershipDataSync,
    isOptedIn,
    canOptIn,
    setOptedIn,
    syncAllNow,
    resetDatabase,
  } = useOfflineDiagnostics();

  const tableColumns: Column<OfflineCollectionInfo>[] = [
    { id: 'name', label: 'Collection' },
    { id: 'count', label: 'Documents' },
  ];

  const renderSyncAlert = (
    label: string,
    sync: { error?: string | null; lastSyncTime?: string }
  ) => (
    <Alert severity={sync.error ? 'error' : 'info'}>
      {label}: {renderSyncStatus(sync)}
    </Alert>
  );

  return (
    <SectionWrapper
      maxWidth="sm"
      width="100%"
      header="Offline Sync Diagnostics"
      subheader={`Database: ${dbName}`}
      actionsButtons={{
        left: {
          component: Link,
          to: '/schedule',
          label: 'Schedule',
        },
        right: {
          component: LoadingButton,
          onClick: syncAllNow,
          label: isSyncing ? 'Syncing…' : 'Force Resync',
          loading: isLoading || isSyncing,
        },
      }}
    >
      <FlexBox gap={3} flexDirection="column" width="100%">
        <Divider />

        <FlexBox flexDirection="column" gap={1}>
          <Typography variant="subtitle2">Offline Data Mode</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOptedIn}
                disabled={!canOptIn}
                onChange={(e) => setOptedIn(e.target.checked)}
              />
            }
            label="Use offline data for pricing &amp; costs calculations"
          />
          <Typography
            variant="caption"
            color={canOptIn ? 'text.secondary' : 'warning.main'}
          >
            {canOptIn
              ? 'Offline data is available. Toggle above to switch between local and live data.'
              : 'Run Force Resync above to enable this option.'}
          </Typography>
        </FlexBox>

        <Divider />

        <FlexBox flexDirection="column" gap={2}>
          <Typography variant="subtitle2">Sync Status</Typography>
          {renderSyncAlert('Pricebook Sync', pricebookSync)}
          {renderSyncAlert('Lookups Sync', lookupsSync)}
          {renderSyncAlert('Modifiers Sync', modifiersSync)}
          {renderSyncAlert('Membership Data Sync', membershipDataSync)}
        </FlexBox>

        <Divider />

        <FlexBox flexDirection="column" gap={1}>
          <Typography variant="subtitle2">Device Storage</Typography>
          {storage?.supported ? (
            <>
              <Typography variant="body2">
                {formatBytes(storage.usageBytes)} used of{' '}
                {formatBytes(storage.quotaBytes)}
              </Typography>
              <Typography
                variant="caption"
                color={storage.persisted ? 'text.secondary' : 'warning.main'}
              >
                {renderPersistence(storage)}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This browser does not report storage usage.
            </Typography>
          )}
        </FlexBox>

        <Divider />

        <ResponsiveTable
          columns={tableColumns}
          rows={collections}
          noDataMessage="No collections found"
          containerProps={{ width: '100%' }}
        />

        <Divider />

        <FlexBox gap={2} alignItems="center">
          <Typography variant="body2" color="error" gutterBottom>
            Danger zone
          </Typography>
          <Button variant="outlined" color="error" onClick={resetDatabase}>
            Reset Database
          </Button>
        </FlexBox>
      </FlexBox>
    </SectionWrapper>
  );
};

export default OfflineDiagnosticsPage;
